import {
  Participant as RawParticipant,
  Conversation as RawConversation,
  Message as RawMessage,
  User as RawUser,
} from '@twilio/conversations';
import {
  Conversation,
  Message,
  Participant,
  User,
} from '../../shared/TwilioConversationsTypes';

/**
 * Because postMessage only accepts serailizable data, class methods of the Twilio data
 * types cannot be used directly. Instead, they need to be converted to raw objects while
 * maintaining most of the attributes. The original data type has a mix of undefined and
 * null, all nulls are converted to undefined by this serializer.
 */
export class Serializer {
  static async fromParticipant(
    participant: RawParticipant,
  ): Promise<Participant> {
    return {
      sid: participant.sid,
      attributes: participant.attributes ?? undefined,
      dateUpdated: participant.dateUpdated ?? undefined,
      dateCreated: participant.dateCreated ?? undefined,
      identity: participant.identity ?? undefined,
      isTyping: participant.isTyping,
      lastReadMessageIndex: participant.lastReadMessageIndex ?? undefined,
      type: participant.type,
      user: this.fromUser(await participant.getUser()),
      conversationId: participant.conversation.sid,
    };
  }

  static fromUser(user: RawUser): User {
    return {
      identity: user.identity,
      attributes: user.attributes ?? undefined,
      friendlyName: user.friendlyName ?? undefined,
      isOnline: user.isOnline ?? undefined,
      isNotifiable: user.isNotifiable ?? undefined,
      isSubscribed: user.isSubscribed,
    };
  }

  static fromConversation(conversation: RawConversation): Conversation {
    return {
      sid: conversation.sid,
      uniqueName: conversation.uniqueName ?? undefined,
      status: conversation.status,
      friendlyName: conversation.friendlyName ?? undefined,
      dateUpdated: conversation.dateUpdated ?? undefined,
      dateCreated: conversation.dateCreated ?? undefined,
      createdBy: conversation.createdBy,
      attributes: conversation.attributes ?? undefined,
      lastReadMessageIndex: conversation.lastReadMessageIndex ?? undefined,
      lastMessage: conversation.lastMessage ?? undefined,
      state: conversation.state,
    };
  }

  static async fromMessage(message: RawMessage): Promise<Message> {
    let participant: RawParticipant | undefined;
    try {
      participant = await message.getParticipant();
    } catch (e) {
      participant = undefined; // the message participant left the conversation
    }
    return {
      sid: message.sid,
      conversationId: message.conversation.sid,
      author: message.author ?? undefined,
      body: message.body ?? undefined,
      dateUpdated: message.dateUpdated ?? undefined,
      index: message.index,
      lastUpdatedBy: message.lastUpdatedBy ?? undefined,
      dateCreated: message.dateCreated ?? undefined,
      attributes: message.attributes ?? undefined,
      type: message.type,
      participantSid: message.participantSid ?? undefined,
      participant: participant
        ? await this.fromParticipant(participant)
        : undefined,
      attachedMedia: message.attachedMedia
        ? await Promise.all(
            message.attachedMedia.map(async (m) => ({
              sid: m.sid,
              filename: m.filename ?? undefined,
              contentType: m.contentType,
              size: m.size,
              category: m.category,
              url: (await m.getContentTemporaryUrl()) ?? '',
            })),
          )
        : undefined,
    };
  }
}
